import { SHEET, TITLE_BLOCK } from './constants';
import type { DrawingMetadata, ComponentDrawing } from '@shared/types';

export type ViewRect = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export type SheetLayout = {
  views: Map<string, ViewRect>;
  titleBlock: ViewRect;
  bomArea: ViewRect | null;
  sheetSize: { w: number; h: number };
};

/**
 * Fallback 4-quadrant layout when no DrawingMetadata is available.
 * Matches the original getViewLayout() behavior.
 */
export function getFallbackLayout(
  sheetW: number,
  sheetH: number,
  margin: number,
): SheetLayout {
  const tbW = TITLE_BLOCK.width;
  const tbH = TITLE_BLOCK.height;
  const drawAreaW = sheetW - margin * 2;
  const drawAreaH = sheetH - margin * 2 - tbH;
  const halfW = drawAreaW / 2;
  const halfH = drawAreaH / 2;

  const views = new Map<string, ViewRect>();
  views.set('top', { x: margin, y: margin, w: halfW, h: halfH });
  views.set('front', { x: margin, y: margin + halfH, w: halfW, h: halfH });
  views.set('right', { x: margin + halfW, y: margin + halfH, w: halfW, h: halfH });
  views.set('isometric', { x: margin + halfW, y: margin, w: halfW, h: halfH });

  return {
    views,
    titleBlock: {
      x: sheetW - margin - tbW,
      y: sheetH - margin - tbH,
      w: tbW,
      h: tbH,
    },
    bomArea: null,
    sheetSize: { w: sheetW, h: sheetH },
  };
}

/**
 * Compute layout from DrawingMetadata for a given sheet number.
 * Places views according to JIS third-angle projection rules:
 * - Front view: center-left anchor
 * - Top: above front
 * - Right: right of front
 * - Left: left of front
 * - Rear: far right
 * - Sections: below main views
 * - Isometric: top-right
 * - BOM: right column if present
 */
export function computeLayout(
  metadata: DrawingMetadata,
  sheetNumber: number,
  sheetW: number,
  sheetH: number,
  margin: number,
): SheetLayout {
  const tbW = TITLE_BLOCK.width;
  const tbH = TITLE_BLOCK.height;

  const titleBlock: ViewRect = {
    x: sheetW - margin - tbW,
    y: sheetH - margin - tbH,
    w: tbW,
    h: tbH,
  };

  // Get allocation for this sheet
  const allocation = metadata.sheetAllocation?.find(
    (a) => a.sheetNumber === sheetNumber,
  );

  // Normalize AI-generated view references to standard view type names
  const VIEW_NAME_MAP: Record<string, string> = {
    '平面図': 'top', 'top': 'top', 'top_view': 'top',
    '正面図': 'front', 'front': 'front', 'front_view': 'front',
    '右側面図': 'right', 'right': 'right', 'right_view': 'right',
    '左側面図': 'left', 'left': 'left', 'left_view': 'left',
    '背面図': 'rear', 'rear': 'rear', 'rear_view': 'rear',
    '底面図': 'bottom', 'bottom': 'bottom', 'bottom_view': 'bottom',
    'アイソメ図': 'isometric', 'isometric': 'isometric', 'iso': 'isometric', 'isometric_view': 'isometric',
  };

  function normalizeViewRef(ref: string): string {
    // Direct match
    if (VIEW_NAME_MAP[ref]) return VIEW_NAME_MAP[ref];
    // Case-insensitive match
    const lower = ref.toLowerCase();
    if (VIEW_NAME_MAP[lower]) return VIEW_NAME_MAP[lower];
    // Section views pass through
    if (ref.startsWith('section-') || ref.includes('断面')) return ref;
    // Fuzzy match: strip parenthetical suffixes like （南面）, then re-check
    const stripped = ref.replace(/[（(][^）)]*[）)]/g, '').trim();
    if (VIEW_NAME_MAP[stripped]) return VIEW_NAME_MAP[stripped];
    // Prefix match: check if ref starts with a known Japanese name
    for (const [key, value] of Object.entries(VIEW_NAME_MAP)) {
      if (ref.startsWith(key) || ref.includes(key)) return value;
    }
    // Fallback: return as-is
    console.warn(`[layoutEngine] Unknown view reference: "${ref}" — passing through`);
    return ref;
  }

  // If no allocation or sheet 1 with no allocation, use smart auto-layout
  let viewsOnSheet: string[];
  if (allocation) {
    const rawRefs = allocation.contents
      .filter((c) => c.type === 'view' || c.type === 'section')
      .map((c) => normalizeViewRef(c.reference));
    // If allocation references didn't yield any standard views, fall back to recommendedViews
    const standardViews = ['top', 'front', 'right', 'left', 'rear', 'bottom', 'isometric'];
    const hasStandardViews = rawRefs.some(r => standardViews.includes(r) || r.startsWith('section-'));
    if (hasStandardViews) {
      viewsOnSheet = rawRefs;
    } else {
      console.warn(`[layoutEngine] Sheet ${sheetNumber} allocation has no standard views (refs=[${rawRefs.join(', ')}]). Falling back to recommendedViews.`);
      viewsOnSheet = metadata.recommendedViews?.map((v) => v.type) ?? ['top', 'front', 'right', 'isometric'];
    }
  } else {
    viewsOnSheet = metadata.recommendedViews?.map((v) => v.type) ?? ['top', 'front', 'right', 'isometric'];
  }

  const hasBOM = allocation
    ? allocation.contents.some((c) => c.type === 'bom')
    : sheetNumber === 1 && (metadata.bom?.length ?? 0) > 0;

  // Compute available drawing area
  const bomWidth = hasBOM ? 180 : 0;
  const drawAreaW = sheetW - margin * 2 - bomWidth;
  const drawAreaH = sheetH - margin * 2 - tbH;

  // BOM area (right side, above title block)
  const bomArea: ViewRect | null = hasBOM
    ? {
        x: sheetW - margin - bomWidth,
        y: margin,
        w: bomWidth,
        h: sheetH - margin * 2 - tbH,
      }
    : null;

  // Determine primary and secondary views
  const primaryViews = metadata.recommendedViews
    ?.filter((v) => v.isPrimary && viewsOnSheet.includes(v.type))
    .map((v) => v.type) ?? [];
  const primaryViewNames: string[] = primaryViews;
  const secondaryViews = viewsOnSheet.filter((v) => !primaryViewNames.includes(v));

  // Layout views in a grid
  const views = new Map<string, ViewRect>();
  const allViews = [...primaryViews, ...secondaryViews];

  console.log(`[layoutEngine] computeLayout: sheet=${sheetNumber}, viewsOnSheet=[${viewsOnSheet.join(', ')}], primary=[${primaryViews.join(', ')}], secondary=[${secondaryViews.join(', ')}], hasBOM=${hasBOM}, bomWidth=${bomWidth}, drawArea=${drawAreaW}x${drawAreaH}`);

  if (allViews.length === 0) {
    console.warn(`[layoutEngine] computeLayout: NO views to layout! recommendedViews=${JSON.stringify(metadata.recommendedViews)}, sheetAllocation=${JSON.stringify(allocation)}`);
    return { views, titleBlock, bomArea, sheetSize: { w: sheetW, h: sheetH } };
  }

  // Use JIS third-angle positioning
  const viewPositions = assignJISPositions(allViews, drawAreaW, drawAreaH, margin);
  for (const [viewName, rect] of viewPositions) {
    views.set(viewName, rect);
    console.log(`[layoutEngine] View "${viewName}" → x=${rect.x.toFixed(0)}, y=${rect.y.toFixed(0)}, w=${rect.w.toFixed(0)}, h=${rect.h.toFixed(0)}`);
  }

  return { views, titleBlock, bomArea, sheetSize: { w: sheetW, h: sheetH } };
}

/**
 * Assign view positions following JIS third-angle projection conventions.
 */
function assignJISPositions(
  viewNames: string[],
  areaW: number,
  areaH: number,
  margin: number,
): Map<string, ViewRect> {
  const result = new Map<string, ViewRect>();

  // Categorize views
  const hasTop = viewNames.includes('top');
  const hasFront = viewNames.includes('front');
  const hasRight = viewNames.includes('right');
  const hasLeft = viewNames.includes('left');
  const hasRear = viewNames.includes('rear');
  const hasIso = viewNames.includes('isometric');
  const hasBottom = viewNames.includes('bottom');
  const sectionViews = viewNames.filter((v) => v.includes('断面') || v.startsWith('section-'));

  // Determine grid: rows × cols based on what views exist
  const cols = 2 + (hasLeft ? 1 : 0) + (hasRear ? 1 : 0);
  const rows = 2 + (hasBottom ? 1 : 0);

  const cellW = areaW / Math.max(cols, 2);
  const cellH = areaH / Math.max(rows + (sectionViews.length > 0 ? 1 : 0), 2);

  // Front view: column 1 (or 2 if left exists), row 1
  const frontCol = hasLeft ? 1 : 0;
  const frontRow = hasTop ? 1 : 0;

  if (hasFront) {
    result.set('front', {
      x: margin + frontCol * cellW,
      y: margin + frontRow * cellH,
      w: cellW,
      h: cellH,
    });
  }

  if (hasTop) {
    result.set('top', {
      x: margin + frontCol * cellW,
      y: margin,
      w: cellW,
      h: cellH,
    });
  }

  if (hasRight) {
    result.set('right', {
      x: margin + (frontCol + 1) * cellW,
      y: margin + frontRow * cellH,
      w: cellW,
      h: cellH,
    });
  }

  if (hasLeft) {
    result.set('left', {
      x: margin,
      y: margin + frontRow * cellH,
      w: cellW,
      h: cellH,
    });
  }

  if (hasRear) {
    const rearCol = frontCol + 2;
    result.set('rear', {
      x: margin + rearCol * cellW,
      y: margin + frontRow * cellH,
      w: cellW,
      h: cellH,
    });
  }

  if (hasBottom) {
    result.set('bottom', {
      x: margin + frontCol * cellW,
      y: margin + (frontRow + 1) * cellH,
      w: cellW,
      h: cellH,
    });
  }

  if (hasIso) {
    // Top-right corner
    const isoCol = hasRight ? frontCol + 2 : frontCol + 1;
    result.set('isometric', {
      x: margin + isoCol * cellW,
      y: margin,
      w: cellW,
      h: cellH,
    });
  }

  // Section views: bottom row
  const sectionRow = frontRow + 1 + (hasBottom ? 1 : 0);
  sectionViews.forEach((sv, i) => {
    result.set(sv, {
      x: margin + i * cellW,
      y: margin + sectionRow * cellH,
      w: cellW,
      h: cellH,
    });
  });

  return result;
}

/**
 * Compute layout for component-level drawings.
 * Each component gets its own views arranged in a grid.
 * Layout: each component gets a "row" with its views side by side.
 */
export function computeComponentLayout(
  components: ComponentDrawing[],
  sheetW: number,
  sheetH: number,
  margin: number,
  sheetNumber: number = 1,
): SheetLayout {
  const tbW = TITLE_BLOCK.width;
  const tbH = TITLE_BLOCK.height;

  // BOM on the right side (only on sheet 1)
  const bomW = sheetNumber === 1 ? 180 : 0;
  const drawAreaW = sheetW - margin * 2 - bomW;
  const drawAreaH = sheetH - margin * 2 - tbH;

  const views = new Map<string, ViewRect>();

  // Group components for the current sheet
  // Max ~4 components per sheet to keep readable
  const COMPONENTS_PER_SHEET = 4;
  const startIdx = (sheetNumber - 1) * COMPONENTS_PER_SHEET;
  const sheetComponents = components.slice(startIdx, startIdx + COMPONENTS_PER_SHEET);

  if (sheetComponents.length === 0) {
    return {
      views,
      titleBlock: {
        x: sheetW - margin - tbW,
        y: sheetH - margin - tbH,
        w: tbW,
        h: tbH,
      },
      bomArea: sheetNumber === 1 ? {
        x: sheetW - margin - bomW,
        y: margin,
        w: bomW,
        h: drawAreaH,
      } : null,
      sheetSize: { w: sheetW, h: sheetH },
    };
  }

  // Calculate grid: each component gets a row, views are columns
  const maxViewsPerComponent = Math.max(...sheetComponents.map(c => c.views.length), 1);
  const cols = Math.min(maxViewsPerComponent + 1, 4); // +1 for component label, max 4 cols
  const rows = sheetComponents.length;

  const cellW = drawAreaW / cols;
  const cellH = drawAreaH / rows;

  sheetComponents.forEach((comp, rowIdx) => {
    comp.views.forEach((view, colIdx) => {
      const key = `${comp.componentId}_${view}`;
      views.set(key, {
        x: margin + (colIdx) * cellW,
        y: margin + rowIdx * cellH,
        w: cellW,
        h: cellH,
      });
    });
  });

  return {
    views,
    titleBlock: {
      x: sheetW - margin - tbW,
      y: sheetH - margin - tbH,
      w: tbW,
      h: tbH,
    },
    bomArea: sheetNumber === 1 ? {
      x: sheetW - margin - bomW,
      y: margin,
      w: bomW,
      h: drawAreaH,
    } : null,
    sheetSize: { w: sheetW, h: sheetH },
  };
}

/**
 * Calculate how many sheets are needed for component drawings.
 */
export function getComponentSheetCount(components: ComponentDrawing[]): number {
  const COMPONENTS_PER_SHEET = 4;
  return Math.max(1, Math.ceil(components.length / COMPONENTS_PER_SHEET));
}
